import React, { useState, useMemo, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ExternalLink, Stethoscope, CheckCircle2, Star, FileText, HeadphonesIcon, Search, ChevronDown } from "lucide-react";

type Service = { value: string; label: string; price: string; image: string; price_id: string; desc: string };
type Props = { onComplete: (data: Service) => void };

const PRODUCTS = {
  Radiology: process.env.REACT_APP_PRICE_RADIOLOGY,
  Neurology: process.env.REACT_APP_PRICE_NEUROLOGY,
  Cardiology: process.env.REACT_APP_PRICE_CARDIOLOGY,
  Oncology: process.env.REACT_APP_PRICE_ONCOLOGY,
};

const SERVICES: Service[] = [
  { value: 'Radiology', label: 'Radiology', price: '$199', desc: 'Imaging & diagnostic review', image: '/static/images/placeholders/covers/Radiology.png', price_id: PRODUCTS.Radiology },
  { value: 'Neurology', label: 'Neurology', price: '$399', desc: 'Brain & nervous system', image: '/static/images/placeholders/covers/neurology.png', price_id: PRODUCTS.Neurology },
  { value: 'Cardiology', label: 'Cardiology', price: '$399', desc: 'Heart & cardiovascular', image: '/static/images/placeholders/covers/cardiology.png', price_id: PRODUCTS.Cardiology },
  { value: 'Oncology', label: 'Oncology', price: '$399', desc: 'Cancer diagnosis & treatment', image: '/static/images/placeholders/covers/oncology.png', price_id: PRODUCTS.Oncology },
];

const FEATURES = [
  { icon: Star, label: 'Expert Physician' },
  { icon: FileText, label: 'Detailed Report' },
  { icon: HeadphonesIcon, label: 'Follow-up Support' },
];

/* ═══════════════════════════════════════════════════════════════
   COMPONENT — Premium searchable combobox (scalable for 50+)
   ═══════════════════════════════════════════════════════════════ */

const SelectService: React.FC<Props> = ({ onComplete }) => {
  const navigate = useNavigate();
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  const filtered = useMemo(() => {
    if (!searchQuery) return SERVICES;
    const q = searchQuery.toLowerCase();
    return SERVICES.filter(s => s.label.toLowerCase().includes(q) || s.desc.toLowerCase().includes(q));
  }, [searchQuery]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) setIsOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSelect = (service: Service) => {
    setSelectedService(service);
    setIsOpen(false);
    setSearchQuery('');
    setTimeout(() => onComplete(service), 0);
  };

  return (
    <div className="w-full mx-auto flex flex-col gap-4 sm:gap-6 py-1 sm:py-2">

      {/*
        The badge sits beside the TITLE, not the whole block. Aligned against
        the two-line subtitle it left a ragged gap and pushed the heading into
        an awkward wrap on a phone.
      */}
      <div>
        <div className="flex items-center justify-between gap-3">
          <h3 className="text-[15px] sm:text-base font-bold text-slate-900 dark:text-slate-100">Select Medical Specialty</h3>
          <span className="flex-shrink-0 text-[10px] sm:text-xs font-bold px-2 sm:px-2.5 py-1 sm:py-1.5 rounded-lg uppercase tracking-[0.1em] ring-1 text-brand-600 bg-brand-50 ring-brand-300/40 dark:text-brand-300 dark:bg-brand-500/[0.12] dark:ring-brand-400/20">
            Required
          </span>
        </div>
        <p className="text-[13px] sm:text-sm mt-1 text-slate-500 dark:text-slate-400">Choose the service for your second opinion</p>
      </div>

      {/* ═══ SEARCHABLE COMBOBOX ═══ */}
      <div className="relative" ref={dropdownRef}>
        <button
          type="button"
          onClick={() => setIsOpen(o => !o)}
          className={[
            'w-full min-h-[56px] flex items-center justify-between gap-2 px-3 sm:px-4 py-3 sm:py-3.5 rounded-xl border-2 transition-all duration-300 text-left group',
            'ease-[cubic-bezier(0.22,1,0.36,1)]',
            'bg-white dark:bg-[#1C2E4C]',
            isOpen
              ? 'border-brand-500 ring-2 ring-brand-500/15 shadow-[0_4px_20px_-4px_rgba(3,93,184,0.20)]'
              : selectedService
                ? 'border-brand-500/30 hover:border-brand-500/50 shadow-sm dark:border-brand-500/40 dark:hover:border-brand-500/60 dark:shadow-[0_4px_24px_-4px_rgba(0,0,0,0.40)]'
                : 'border-slate-300 hover:border-brand-300 dark:border-white/[0.18] dark:hover:border-white/30',
          ].join(' ')}
        >
          {selectedService ? (
            <div className="flex items-center gap-2.5 sm:gap-3.5 flex-1 min-w-0">
              <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors bg-[#BFDBFE] ring-2 ring-brand-300/60 shadow-sm dark:bg-[#2D5299] dark:ring-brand-400/35 dark:shadow-brand-500/10">
                <img src={selectedService.image} alt={selectedService.label} className="w-7 h-7 object-contain drop-shadow-[0_2px_8px_rgba(3,93,184,0.45)] dark:drop-shadow-[0_2px_8px_rgba(147,197,253,0.60)]" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-bold text-sm text-slate-900 dark:text-slate-100 truncate">{selectedService.label}</p>
                {/* The description repeats in the card below, so on a phone it
                    only competes with the label for room. */}
                <p className="hidden sm:block text-ui-sm mt-0.5 truncate text-slate-500 dark:text-slate-400">{selectedService.desc}</p>
              </div>
              <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
                <span className="text-xs sm:text-sm font-bold text-brand-500 bg-brand-500/[0.06] px-2 sm:px-2.5 py-0.5 rounded-lg whitespace-nowrap">{selectedService.price}</span>
                <CheckCircle2 size={18} className="text-brand-500" />
              </div>
            </div>
          ) : (
            <div className="flex min-w-0 flex-1 items-center gap-2.5 text-slate-500 dark:text-slate-400">
              <Search size={16} className="flex-shrink-0 text-slate-400 dark:text-slate-500" />
              <span className="font-medium truncate text-sm sm:text-base">
                <span className="xs:hidden">Select a specialty…</span>
                <span className="hidden xs:inline">Search or select a medical specialty…</span>
              </span>
            </div>
          )}
          <ChevronDown size={18} className={`transition-transform duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] flex-shrink-0 ml-2 text-slate-500 dark:text-slate-400 ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        {/* Dropdown panel */}
        {isOpen && (
          <div className="absolute z-50 mt-1.5 w-full rounded-xl border overflow-hidden animate-fade-in bg-white border-slate-200 shadow-[0_8px_30px_-4px_rgba(0,0,0,0.12),0_20px_50px_-12px_rgba(3,93,184,0.10)] dark:bg-[#0F1E38] dark:border-white/[0.14] dark:shadow-[0_24px_80px_-12px_rgba(0,0,0,0.95)]">

            {/* Search bar */}
            <div className="px-3 pt-3 pb-2">
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
                <input
                  // Focusing on a phone raises the keyboard over the very list
                  // the patient is trying to read, so this is pointer-only.
                  autoFocus={typeof window !== 'undefined'
                    && window.matchMedia('(min-width: 640px) and (pointer: fine)').matches}
                  type="text"
                  placeholder="Search specialties…"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="w-full pl-8 pr-3 py-2 text-sm rounded-lg border outline-none transition-all duration-200 placeholder:text-slate-400 dark:placeholder:text-slate-600 bg-slate-50 border-slate-200 text-slate-800 focus:border-brand-400 focus:bg-white dark:bg-[#162338] dark:border-white/[0.12] dark:text-slate-100 dark:focus:border-brand-500/60 dark:focus:bg-[#1C2E4C]"
                />
              </div>
            </div>

            {/* Scrollable list */}
            <div className="overflow-y-auto max-h-[45vh] sm:max-h-60 overscroll-contain scrollbar-hide">
              {filtered.length === 0 ? (
                <div className="text-center py-8 px-4">
                  <Stethoscope size={20} className="mx-auto mb-2 text-slate-300 dark:text-slate-600" />
                  <p className="text-sm font-semibold text-slate-600 dark:text-slate-300">No services found</p>
                  <p className="text-xs mt-0.5 text-slate-400 dark:text-slate-500">Try a different search term</p>
                </div>
              ) : (
                filtered.map((service, idx) => {
                  const isActive = selectedService?.value === service.value;
                  return (
                    <button
                      key={service.value}
                      type="button"
                      onClick={() => handleSelect(service)}
                      className={[
                        'w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors duration-150 group/item',
                        idx !== 0 ? 'border-t border-slate-100 dark:border-white/[0.06]' : '',
                        isActive
                          ? 'bg-brand-500/[0.07] dark:bg-brand-500/[0.18]'
                          : 'hover:bg-slate-50 dark:hover:bg-white/[0.04]',
                      ].join(' ')}
                    >
                      {/* Service icon — compact */}
                      <div className={[
                        'w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 transition-all duration-150',
                        isActive
                          ? 'bg-[#BFDBFE] ring-2 ring-brand-400/70 shadow-md shadow-brand-500/20 dark:bg-[#2D5299] dark:ring-brand-400/50 dark:shadow-brand-400/20'
                          : 'bg-[#DBEAFE] ring-1 ring-brand-300/50 shadow-sm dark:bg-[#1E3A6E] dark:ring-brand-400/20 group-hover/item:bg-[#BFDBFE] group-hover/item:ring-brand-400/60 dark:group-hover/item:bg-[#2D5299] dark:group-hover/item:ring-brand-400/35',
                      ].join(' ')}>
                        <img src={service.image} alt={service.label} className="w-7 h-7 object-contain drop-shadow-[0_2px_8px_rgba(3,93,184,0.45)] dark:drop-shadow-[0_2px_8px_rgba(147,197,253,0.60)]" />
                      </div>

                      {/* Label + description */}
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-semibold leading-tight ${isActive ? 'text-brand-500 dark:text-brand-400' : 'text-slate-800 dark:text-slate-100'}`}>
                          {service.label}
                        </p>
                        <p className="text-xs mt-0.5 truncate text-slate-400 dark:text-slate-500">{service.desc}</p>
                      </div>

                      {/* Price + check */}
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <span className={`text-sm font-bold tabular-nums ${isActive ? 'text-brand-500 dark:text-brand-400' : 'text-slate-700 dark:text-slate-200'}`}>
                          {service.price}
                        </span>
                        {isActive ? (
                          <CheckCircle2 size={16} className="text-brand-500 dark:text-brand-400" />
                        ) : (
                          <div className="w-4 h-4 rounded-full border-2 border-slate-300 dark:border-slate-600 group-hover/item:border-brand-400 dark:group-hover/item:border-brand-500/50 transition-colors" />
                        )}
                      </div>
                    </button>
                  );
                })
              )}
            </div>

            {/* Footer with count */}
            <div className="px-3 py-2 border-t border-slate-100 dark:border-white/[0.08] flex items-center justify-between bg-slate-50/60 dark:bg-[#0C1828]">
              <p className="text-xs font-medium text-slate-400 dark:text-slate-500">
                Showing {filtered.length} of {SERVICES.length} specialties
              </p>
              {searchQuery && (
                <button
                  type="button"
                  onClick={() => setSearchQuery('')}
                  className="text-xs font-semibold text-brand-500 hover:text-brand-600 dark:text-brand-400 dark:hover:text-brand-300 transition-colors"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ═══ SELECTED SERVICE PREVIEW ═══ */}
      {selectedService ? (
        <div
          key={selectedService.value}
          className="rounded-2xl overflow-hidden border card-shine bg-white border-slate-200 shadow-elevation-2 dark:bg-[#162338] dark:border-white/[0.12] dark:shadow-[0_4px_24px_-4px_rgba(0,0,0,0.60)]"
        >
          {/* Gradient accent strip */}
          <div className="h-[3px] bg-gradient-to-r from-brand-700 via-brand-500 to-brand-300" />

          <div className="flex flex-col sm:flex-row">
            {/*
              Phone: a compact identity strip — thumbnail, name and price on one
              row. The full-bleed artwork panel below `sm` cost ~150px of a
              390px screen to show a decorative icon, pushing the real content
              (price, features, CTA) below the fold.
              Tablet and up: the original artwork panel returns unchanged.
            */}
            <div className="relative flex items-center gap-3.5 border-b border-slate-100 px-4 py-3 dark:border-white/[0.06] sm:min-h-[200px] sm:w-[38%] sm:justify-center sm:border-b-0 sm:px-8 sm:py-0 bg-white dark:bg-[#0F1E38]">
              {/* Subtle radial bg glow */}
              <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(43,141,224,0.08)_0%,transparent_70%)] dark:bg-[radial-gradient(ellipse_at_center,rgba(43,141,224,0.18)_0%,transparent_70%)]" />
              <div className="absolute w-36 h-36 rounded-full bg-brand-400/[0.06] dark:bg-brand-400/[0.12] blur-3xl top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" />

              <div className="relative z-10 flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-brand-50 ring-1 ring-brand-200/60 dark:bg-[#2D5299] dark:ring-brand-400/30 sm:h-auto sm:w-auto sm:rounded-none sm:bg-transparent sm:ring-0">
                <img
                  src={selectedService.image}
                  alt={selectedService.label}
                  className="h-8 w-8 sm:h-32 sm:w-auto object-contain drop-shadow-[0_6px_20px_rgba(43,141,224,0.25)] dark:drop-shadow-[0_6px_28px_rgba(43,141,224,0.40)]"
                />
              </div>

              {/* Identity — phone only; the panel below repeats it from `sm`. */}
              <div className="relative z-10 min-w-0 flex-1 sm:hidden">
                <h4 className="truncate text-[15px] font-bold text-slate-900 dark:text-slate-100">{selectedService.label}</h4>
                <p className="mt-0.5 text-[13px] font-bold text-brand-500">
                  {selectedService.price}
                  <span className="ml-1 font-medium text-slate-400 dark:text-slate-500">one-time</span>
                </p>
              </div>
              <CheckCircle2 size={18} className="relative z-10 flex-shrink-0 text-brand-500 sm:hidden" />
            </div>

            {/* Details */}
            <div className="flex-1 min-w-0 px-4 sm:px-7 py-4 sm:py-7 flex flex-col justify-center space-y-2.5 sm:space-y-3">
              <div className="hidden sm:flex items-start justify-between gap-3 sm:gap-4">
                  <h4 className="text-base sm:text-lg font-bold text-slate-900 dark:text-slate-100 min-w-0 break-words">{selectedService.label}</h4>
                <div className="text-right flex-shrink-0">
                  <span className="text-xl sm:text-2xl font-bold text-brand-500 drop-shadow-sm whitespace-nowrap">{selectedService.price}</span>
                  <p className="text-ui-sm mt-0.5 text-slate-500 dark:text-slate-400">one-time fee</p>
                </div>
              </div>

              <p className="text-[13.5px] sm:text-sm leading-relaxed text-slate-600 dark:text-slate-200">{selectedService.desc}</p>

              {/* Feature badges — compact enough to sit two-up on a phone. */}
              <div className="flex flex-wrap gap-1.5 sm:gap-2 pt-0.5">
                {FEATURES.map(({ icon: Icon, label }) => (
                  <div key={label} className="flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1 sm:py-1.5 rounded-lg border transition-colors bg-brand-50 border-brand-200 hover:border-brand-300 dark:bg-[#1C2E4C] dark:border-white/[0.18] dark:hover:border-white/30">
                    <Icon size={12} className="text-brand-500 flex-shrink-0" />
                    <span className="text-[11px] sm:text-ui-sm font-semibold text-slate-600 dark:text-slate-200 whitespace-nowrap">{label}</span>
                  </div>
                ))}
              </div>

              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/services/${selectedService.value.toLowerCase()}`);
                }}
                className="inline-flex min-h-[44px] sm:min-h-0 items-center gap-1.5 -my-2 sm:my-0 py-2 sm:py-0 text-sm font-bold text-brand-500 hover:text-brand-600 dark:hover:text-brand-400 transition-colors w-fit sm:pt-0.5 group"
              >
                Learn more about this service <ExternalLink size={13} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
            </div>
          </div>
        </div>
      ) : (
        /* Empty state */
        <div className="rounded-2xl border flex flex-col items-center justify-center py-10 sm:py-16 px-5 sm:px-6 text-center relative overflow-hidden border-slate-200 bg-slate-50 dark:border-white/[0.10] dark:bg-[#162338]">
          <div className="absolute inset-0 opacity-[0.03] [background-image:radial-gradient(circle_at_1px_1px,currentColor_1px,transparent_0)] [background-size:24px_24px]" />
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-5 relative z-10 bg-white border border-slate-200 shadow-sm dark:bg-[#1C2E4C] dark:border-white/[0.12]">
            <Stethoscope size={24} className="text-brand-400 dark:text-slate-400" />
          </div>
          <p className="text-base font-bold relative z-10 text-slate-600 dark:text-slate-300">Select a service to see details</p>
          <p className="text-sm mt-1.5 relative z-10 text-slate-400 dark:text-slate-500">Search or browse the specialties above</p>
        </div>
      )}
    </div>
  );
};

export default SelectService;
